import { createAsyncThunk } from '@reduxjs/toolkit';
import { http, type PlatformType } from '@core';
import type {
  CalculateOrderCommand,
  CalculateOrderResponse,
} from '../models/calculation.types';

/**
 * Расчёт стоимости заказа
 */
export const calculateOrder = createAsyncThunk<
  CalculateOrderResponse,
  CalculateOrderCommand,
  { rejectValue: string }
>('checkout/calculateOrder', async ({ reason: _reason, ...params }, { rejectWithValue }) => {
  try {
    const response = await http.post<CalculateOrderResponse>('/api/order/calculate', params);
    return response.data;
  } catch (error) {
    return rejectWithValue('Не удалось рассчитать стоимость заказа');
  }
});

interface FormOrderPayload {
  showId: number;
  eventId: number;
  customer: {
    firstName: string;
    middleName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  promoCode?: string;
  comment: string;
  insurance: boolean;
  seatIds: number[];
}

interface FormOrderParams {
  formData: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    promoCode?: string;
  };

  showId: number;
  eventId: number;
  platformType: PlatformType;
  insurance: boolean;
  comment?: string;
  seatIds: number[];
}

interface FormOrderResponse {
  orderId: number;
  createdAt: string;
  validDateTime: string;
}

/**
 * формирование заказа
 */
export const formOrder = createAsyncThunk<FormOrderResponse, FormOrderParams>(
  'checkout/formOrder',
  async ({ formData, seatIds, showId, eventId, platformType, insurance }, { rejectWithValue }) => {
    try {
      const payload: FormOrderPayload = {
        showId,
        eventId,
        customer: {
          firstName: formData.firstName,
          middleName: '',
          lastName: formData.lastName,
          phone: formData.phone,
          email: formData.email,
        },
        promoCode: formData.promoCode,
        comment: '',
        insurance,
        seatIds,
      };

      const response = await http.post<FormOrderResponse>(
        '/api/order/form_order_by_seats',
        payload,
        {
          headers: {
            'X-Web-Platform-Type': platformType,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue('Не удалось оформить заказ');
    }
  }
);

interface PaymentUrlResponse {
  url: string;
}

/**
 * Получение URL для оплаты заказа
 */
export const getPaymentUrl = createAsyncThunk<string, number>(
  'checkout/getPaymentUrl',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await http.get<PaymentUrlResponse>(`/api/order/${orderId}/payment`);
      return response.data.url;
    } catch (error) {
      return rejectWithValue('Не удалось получить URL для оплаты');
    }
  }
);

export interface OrderDetails {
  id: string;
  number: number;
  createdAt: string;
  eventNames: string[];
  ticketsCount: number;
  seatsCount: number;
  amount: number;
  status: string;
  statusStr: string;
  voidDateTime: string;
  deliveryStatusStr: string;
  paymentStatusStr: string;
  paymentStatus: string;
  deliveryStatus: string;
}

/**
 * Комбинированное действие для создания заказа и получения URL для оплаты
 */
export const createOrderAndGetPaymentUrl = createAsyncThunk<
  string, // Return type: payment URL
  {
    formData: {
      firstName: string;
      lastName: string;
      phone: string;
      email: string;
      promoCode?: string;
    };
    showId: number;
    eventId: number;
    platformType: PlatformType;
    insurance: boolean;
    seatIds: number[];
  },
  { rejectValue: string }
>('checkout/createOrderAndGetPaymentUrl', async (params, { dispatch, rejectWithValue }) => {
  try {
    // Шаг 1: Создаем заказ
    const orderResult = await dispatch(formOrder(params)).unwrap();

    if (!orderResult?.orderId) {
      throw new Error('Не удалось получить данные заказа');
    }

    // Шаг 2: Получаем URL для оплаты
    const paymentUrl = await dispatch(getPaymentUrl(orderResult.orderId)).unwrap();

    if (!paymentUrl) {
      throw new Error('Не удалось получить URL для оплаты');
    }

    return paymentUrl;
  } catch (error: any) {
    const errorMessage = error.message || 'Произошла ошибка при оформлении заказа';
    return rejectWithValue(errorMessage);
  }
});
